import { type ChangeEvent, type SubmitEvent, useState } from 'react';
import { Link } from 'react-router';
import axios, { AxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { type Region, useRegions } from '@/hooks/useRegions';
import { useDivisions } from '@/hooks/useDivisions';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import ConfirmationModal from '@/components/ConfirmationModal';
import ResourceTags from '@/components/ResourceTags';
import { config } from '@/config';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { AppSheet } from '@/components/ui/app-sheet';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface CreateRegionRequest {
  name: string;
  slug: string;
  divisionId?: string | null;
}

interface UpdateRegionRequest {
  name?: string;
}

interface ApiErrorResponse {
  error: string;
  description?: string;
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function Regions() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingRegion, setEditingRegion] = useState<Region | null>(null);
  const [isDeleteConfirmActive, setIsDeleteConfirmActive] = useState(false);
  const [regionToDelete, setRegionToDelete] = useState<Region | null>(null);
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());

  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formDivisionId, setFormDivisionId] = useState<string>('none');

  const [editFormName, setEditFormName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: regions, isLoading, isError, error } = useRegions(organizationId);
  const { data: divisions, isLoading: isLoadingDivisions } = useDivisions(organizationId);
  const { data: resourceLimits } = useResourceLimits();
  const regionLimits = resourceLimits?.regions;

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  const createRegionMutation = useMutation<void, AxiosError<ApiErrorResponse>, CreateRegionRequest>({
    mutationFn: async (newRegion) => {
      await axios.post(`${config.backendApi}/api/regions`, newRegion, {
        withCredentials: true,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['regions'] });
      setIsCreateOpen(false);
      setFormName('');
      setFormSlug('');
      setFormDivisionId('none');
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description ?? err.response?.data.error ?? 'Failed to create region.',
      );
    },
  });

  const updateRegionMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    { id: string; data: UpdateRegionRequest }
  >({
    mutationFn: async ({ id, data }) => {
      await axios.put(`${config.backendApi}/api/regions/${id}`, data, {
        withCredentials: true,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['regions'] });
      setIsEditOpen(false);
      setEditingRegion(null);
      setEditFormName('');
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description ?? err.response?.data.error ?? 'Failed to update region.',
      );
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  const deleteRegionMutation = useMutation<void, AxiosError<ApiErrorResponse>, string>({
    mutationFn: async (id) => {
      await axios.delete(`${config.backendApi}/api/regions/${id}`, {
        withCredentials: true,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['regions'] });
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description ?? err.response?.data.error ?? 'Failed to delete region.',
      );
    },
  });

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setFormName(newName);
    setFormSlug(generateSlug(newName));
  };

  const handleCreate = (e: SubmitEvent) => {
    e.preventDefault();
    createRegionMutation.mutate({
      name: formName,
      slug: formSlug,
      divisionId: formDivisionId === 'none' ? null : formDivisionId,
    });
  };

  const handleUpdate = (e: SubmitEvent) => {
    e.preventDefault();
    if (editingRegion) {
      updateRegionMutation.mutate({
        id: editingRegion.id,
        data: { name: editFormName },
      });
    }
  };

  const toggleExpansion = (regionId: string) => {
    setExpandedRegions((prev) => {
      const next = new Set(prev);
      if (next.has(regionId)) {
        next.delete(regionId);
      } else {
        next.add(regionId);
      }
      return next;
    });
  };

  const getOwnerDisplay = (region: Region): string => {
    if (!region.divisionId) return 'Organization';
    return divisions?.find((d) => d.id === region.divisionId)?.name ?? 'Unknown Division';
  };

  if (isLoading || isLoadingDivisions) {
    return (
      <PageContainer>
        <Skeleton className="h-9 w-48 mb-6" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Error loading regions: {error.message}</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const columns: readonly ResponsiveTableColumn<Region>[] = [
    {
      key: 'expand',
      label: '',
      hideAt: 'mobile',
      render: (region) => {
        const expanded = expandedRegions.has(region.id);
        return (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpansion(region.id);
            }}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
        );
      },
    },
    {
      key: 'name',
      label: 'Name',
      hideMobileLabel: true,
      render: (region) => (
        <Link
          to={`/admin/regions/${region.id}`}
          className="font-medium hover:underline"
          onClick={(e) => { e.stopPropagation(); }}
        >
          {region.name}
        </Link>
      ),
    },
    {
      key: 'owner',
      label: 'Owner',
      render: (region) => getOwnerDisplay(region),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Regions"
        subtitle="Group devices, gateways, and credentials by location."
        primary={
          <Button onClick={() => {
            setFormName('');
            setFormSlug('');
            setFormDivisionId('none');
            setSubmitError(null);
            setIsCreateOpen(true);
          }}>
            <Plus className="w-4 h-4" />
            Add region
          </Button>
        }
      />

      {submitError ? (
        <Alert variant="destructive" className="mb-4 relative">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
          <button
            type="button"
            onClick={() => { setSubmitError(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      <ResponsiveTable<Region>
        columns={columns}
        rows={regions ?? []}
        rowKey={(r) => r.id}
        rowActions={(region) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); }}
                aria-label={`Actions for ${region.name}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => { e.stopPropagation(); }}>
              <DropdownMenuItem
                onClick={() => {
                  setEditingRegion(region);
                  setEditFormName(region.name);
                  setIsEditOpen(true);
                  setSubmitError(null);
                }}
                className="gap-2"
              >
                <Pencil className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setRegionToDelete(region);
                  setIsDeleteConfirmActive(true);
                }}
                className="gap-2 text-destructive focus:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        empty={<EmptyState variant="no-data" title="No regions yet" />}
      />

      {/* Inline expansion below the table — preserved from Bulma via
          React state. Each expanded row spills below the table. */}
      {regions
        ?.filter((r) => expandedRegions.has(r.id))
        .map((region) => (
          <div
            key={`exp-${region.id}`}
            className="mt-3 border border-border rounded-lg bg-bg-subtle p-4"
          >
            <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
              {region.name}
            </div>
            <ResourceTags resourceType="region" resourceId={region.id} />
          </div>
        ))}

      {/* Create modal */}
      <AppSheet open={isCreateOpen} onOpenChange={setIsCreateOpen} title="Create new region">
        <AppSheet.Body>
          <form id="region-create-form" onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="region-name">Name</Label>
              <Input
                id="region-name"
                value={formName}
                onChange={handleNameChange}
                placeholder="Region name"
                required
                autoFocus
              />
            </div>
            <input type="hidden" name="slug" value={formSlug} />
            <div className="space-y-1.5">
              <Label htmlFor="region-division">Division</Label>
              <Select value={formDivisionId} onValueChange={setFormDivisionId}>
                <SelectTrigger id="region-division">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (organization-owned)</SelectItem>
                  {divisions?.map((division) => (
                    <SelectItem key={division.id} value={division.id}>
                      {division.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {submitError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
            {regionLimits ? (
              <Alert
                className={cn(
                  regionLimits.atLimit && 'border-destructive bg-destructive/5',
                  regionLimits.approachingLimit && !regionLimits.atLimit && 'border-sev-3 bg-sev-3-soft',
                )}
              >
                <AlertDescription>
                  <strong>Regions:</strong> {regionLimits.currentCount} of {regionLimits.softLimit}{' '}
                  used
                  {regionLimits.atLimit
                    ? '. You have reached your region limit. Please upgrade your plan or contact support.'
                    : null}
                </AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsCreateOpen(false); }}
            disabled={createRegionMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="region-create-form"
            disabled={createRegionMutation.isPending || (regionLimits?.atLimit ?? false)}
          >
            {createRegionMutation.isPending ? 'Creating…' : 'Create region'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      {/* Edit modal */}
      <AppSheet
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        title={`Edit region${editingRegion ? `: ${editingRegion.name}` : ''}`}
      >
        <AppSheet.Body>
          <form id="region-edit-form" onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="region-edit-name">Name</Label>
              <Input
                id="region-edit-name"
                value={editFormName}
                onChange={(e) => { setEditFormName(e.target.value); }}
                required
              />
            </div>
            {submitError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsEditOpen(false); }}
            disabled={updateRegionMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="region-edit-form"
            disabled={updateRegionMutation.isPending}
          >
            {updateRegionMutation.isPending ? 'Updating…' : 'Update region'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <ConfirmationModal
        isActive={isDeleteConfirmActive}
        title="Delete region"
        message={`Are you sure you want to delete the region "${regionToDelete?.name ?? 'Unknown'}"? This action cannot be undone and may affect devices, gateways, and credentials associated with this region.`}
        confirmText="Delete region"
        cancelText="Cancel"
        onConfirm={() => {
          if (regionToDelete) {
            deleteRegionMutation.mutate(regionToDelete.id);
            setIsDeleteConfirmActive(false);
            setRegionToDelete(null);
          }
        }}
        onCancel={() => {
          setIsDeleteConfirmActive(false);
          setRegionToDelete(null);
        }}
        isLoading={deleteRegionMutation.isPending}
        isDanger
      />
    </PageContainer>
  );
}
